import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import CompanyProjectAIForm from "@/components/forms/company/project";
import CompanyFractionalAIForm from "@/components/forms/company/fractional";
import CompanyFullTimeAIForm from "@/components/forms/company/full-time";

import { AdminJobDetails } from "@/queries/admin/job-details";

import { CompanyJobOpening } from "@/types";

enum MODE {
  FRACTIONAL = "FRACTIONAL",
  PROJECT = "PROJECT",
  FULLTIME = "FULL-TIME",
}

interface BaseProps {
  opening?: CompanyJobOpening | AdminJobDetails;
  includeMaskedJobDescription?: boolean;
  companyId?: string;
  onSkip?: () => void;
  onSuccess: () => void;
}

type Props = BaseProps &
  (
    | {
        includeSteps: true;
        startingStep?: number;
        stepTotal?: number;
      }
    | {
        includeSteps?: false;
        startingStep?: never;
        stepTotal?: never;
      }
  );

const AddOpportunityForm = ({
  opening,
  includeMaskedJobDescription,
  companyId,
  includeSteps,
  startingStep = 1,
  stepTotal = 5,
  onSkip,
  onSuccess,
}: Props) => {
  const [mode, setMode] = useState<MODE | undefined>();
  const [modeChoice, setModeChoice] = useState<MODE | undefined>();

  useEffect(() => {
    if (opening) {
      setMode(
        opening.type === "Fractional"
          ? MODE.FRACTIONAL
          : opening.type === "Project"
            ? MODE.PROJECT
            : opening.type === "Full-time"
              ? MODE.FULLTIME
              : undefined,
      );
      setModeChoice(undefined);
    }
  }, [opening]);

  return !mode ? (
    <div className="flex flex-col gap-6 items-center">
      <div className="flex flex-col gap-1 self-stretch items-start">
        {includeSteps && (
          <p className="text-sm font-medium text-inline-link self-start">
            Step {startingStep} of {stepTotal}
          </p>
        )}
        <h1 className="text-3xl text-[#2E2E38] font-bold">
          What type of support are you looking for?
        </h1>
        <p className="text-base text-brand-neutral-black-8">
          Tell us about where you’re at, and what you need, and we’ll match you
          to a seasoned leader.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-base text-[#2E2E38] font-normal" required>
          Support Type
        </Label>
        <RadioGroup
          value={modeChoice}
          onValueChange={(data) => setModeChoice(data as MODE)}
        >
          <RadioGroupItem
            variant="radioBar"
            label="Ongoing support"
            description="Hire a fractional executive for ongoing strategic support, typically a few days per week or month."
            value={MODE.FRACTIONAL}
          />
          <RadioGroupItem
            variant="radioBar"
            label="Project-based help"
            description="Bring in an expert to drive a specific project or initiative from start to finish."
            value={MODE.PROJECT}
          />
          <RadioGroupItem
            variant="radioBar"
            label="Full-time hire"
            description="Bring on a dedicated leader full-time to own a function or initiative long-term."
            value={MODE.FULLTIME}
          />
        </RadioGroup>
      </div>
      <div className="h-[1px] bg-[#E4E7EC] w-full" />
      <div
        className={cn(
          "w-full flex flex-row justify-end",
          onSkip && "justify-between",
        )}
      >
        {onSkip && (
          <Button variant="stroke" onClick={onSkip}>
            Skip for now
          </Button>
        )}
        <Button
          variant="success"
          disabled={!modeChoice}
          onClick={() => setMode(modeChoice)}
        >
          Continue
        </Button>
      </div>
    </div>
  ) : mode === MODE.FRACTIONAL ? (
    <CompanyFractionalAIForm
      companyId={companyId}
      opening={opening}
      modal
      includeMaskedJobDescription={includeMaskedJobDescription}
      includeSteps
      startingStep={2}
      stepTotal={5}
      onCancel={() => {
        setMode(undefined);
        setModeChoice(undefined);
      }}
      onSuccess={onSuccess}
    />
  ) : mode === MODE.PROJECT ? (
    <CompanyProjectAIForm
      companyId={companyId}
      opening={opening}
      modal
      includeSteps
      startingStep={2}
      stepTotal={5}
      onCancel={() => {
        setMode(undefined);
        setModeChoice(undefined);
      }}
      onSuccess={onSuccess}
    />
  ) : mode === MODE.FULLTIME ? (
    <CompanyFullTimeAIForm
      companyId={companyId}
      opening={opening}
      modal
      includeSteps
      startingStep={2}
      stepTotal={5}
      onCancel={() => {
        setMode(undefined);
        setModeChoice(undefined);
      }}
      onSuccess={onSuccess}
    />
  ) : undefined;
};

export default AddOpportunityForm;
